import PageTitle from '../../components/PageTitle';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import ProgressBar from '../../components/ProgressBar';
import { GoogleGenAI } from '@google/genai';
import { getGradeSubjects, request } from '../../utils/remote';
import { useGrades, useNationalExams } from '../../react-query/queries';
import { useAuth } from '../../utils/Auth/AuthProvider';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { FaInfoCircle, FaQuestion, FaEdit } from 'react-icons/fa';
import { ListGroup, Table } from 'flowbite-react';

type Exam = {
  id: number;
  name: string;
  no_ques: number;
  price: string | number;
  status: string;
  time_given: number | null;
  created?: string;
  institution_id?: number | null;
  is_generated?: boolean;
  grade?: string;
  sub?: string;
  school_grade_id?: number | null;
  subject_id?: number | null;
};

export default function GenerateExam() {
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nowGrade, setNowGrade] = useState<number>(0);
  // generate modal + progress state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const progressIntervalRef = useRef<number | null>(null);
  const [modalGrade, setModalGrade] = useState<number | null>(null);
  const [modalSubject, setModalSubject] = useState<string>('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSubjects, setModalSubjects] = useState<Array<{ id: number; name: string }>>([]);
  const [modalSubjectsLoading, setModalSubjectsLoading] = useState(false);
  // grades (ids) that have the currently selected subject for the current institute
  const [modalGradesWithSubject, setModalGradesWithSubject] = useState<number[] | null>(null);
  // modal multi-step: 1 = select grade/subject, 2 = exam details (name, time, description)
  const [modalStep, setModalStep] = useState<number>(1);
  const [modalExamName, setModalExamName] = useState<string>('');
  const [modalExamTime, setModalExamTime] = useState<string>('');
  const [modalExamDescription, setModalExamDescription] = useState<string>('');
  // which grades' contents to include in the exam (multi-select)
  const [modalSelectedGrades, setModalSelectedGrades] = useState<number[]>([]);
  const [modalTotalQuestions, setModalTotalQuestions] = useState<string>('');
  // chapters keyed by grade id
  const [modalChaptersByGrade, setModalChaptersByGrade] = useState<Record<number, any[]>>({});
  // per-chapter question counts keyed by chapter id
  const [modalChapterCounts, setModalChapterCounts] = useState<Record<number, number>>({});
  // which selected grade tab is active in step 4
  const [modalActiveGrade, setModalActiveGrade] = useState<number | null>(null);
  // difficulty selection for generated questions
  const [difficultyMode, setDifficultyMode] = useState<'mixed'|'easy'|'medium'|'hard'|'custom'>('mixed');
  const [difficultyPercents, setDifficultyPercents] = useState<{ easy: number; medium: number; hard: number }>({ easy: 40, medium: 40, hard: 20 });
  // gemini / generation states
  const [geminiKey, setGeminiKey] = useState<string | null>(null);
  const [chapterProgress, setChapterProgress] = useState<Array<{ chapterId: number; title: string; total: number; completed: number; status: 'pending'|'running'|'done'|'error' }>>([]);
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([]);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  // overall progress (single progress bar) and current chapter status text
  const [overallCompletedCount, setOverallCompletedCount] = useState<number>(0);
  const [overallTotalCount, setOverallTotalCount] = useState<number>(0);
  const [currentChapterStatusText, setCurrentChapterStatusText] = useState<string>('');
  // national exam picker
  const [showNationalPicker, setShowNationalPicker] = useState<boolean>(false);
  const [nationalSelectedId, setNationalSelectedId] = useState<number | null>(null);

  const { data: gradeData } = useGrades(token);
  const [nowSubject, setNowSubject] = useState<number | null>(0);
  const [subjects, setSubjects] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem('token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    Promise.all([
      request('/api/user-info/', { headers }),
      request('/api/exam/', { headers }),
    ])
      .then(([userInfo, examsResp]) => {
        if (!mounted) return;
        const instId = userInfo?.institution_id ?? null;
        const allExams: Exam[] = examsResp?.exams ?? [];
        const filtered = allExams.filter((e) => e.is_generated && (instId == null ? true : e.institution_id === instId));
        setExams(filtered);
      })
      .catch((err) => {
        console.error(err);
        if (!mounted) return;
        setError('Failed to load generated exams');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  // fetch subjects for selected grade for the current school
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!nowGrade || nowGrade === 0) {
          // clear subjects when no grade selected
          if (mounted) setSubjects([]);
          return;
        }
        const headers: any = { 'Content-Type': 'application/json' };
        const t = token ?? localStorage.getItem('token');
        if (t) headers['Authorization'] = `Bearer ${t}`;
        let d: any = null
        try {
          d = await request(`/api/school/grades/${nowGrade}/subjects/`, { headers })
        } catch (err) {
          if (mounted) setSubjects([])
          return
        }
        if (!mounted) return;
  // endpoint likely returns an array; support { subjects: [...] } wrapper too
  const list = Array.isArray(d) ? d : (Array.isArray(d?.subjects) ? d.subjects : []);
  // normalize various possible server shapes (subject_name, name, subject_id)
  setSubjects(list.map((s: any) => ({ id: s?.id ?? s?.subject_id ?? 0, name: s?.subject_name ?? s?.name ?? s?.subject ?? String(s?.id ?? '') })));
        // reset selected subject when grade changes
        setNowSubject(0);
      } catch (e) {
        if (mounted) setSubjects([]);
      }
    })();
    return () => { mounted = false; };
  }, [nowGrade, token]);

  // generation controls
  const resetGeneration = () => {
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setIsGenerating(false);
    setProgressStep(0);
    setModalStep(1);
    setModalExamName('');
    setModalExamTime('');
    setModalExamDescription('');
    setModalSelectedGrades([]);
    setModalTotalQuestions('');
    setModalChaptersByGrade({});
    setModalChapterCounts({});
    setModalActiveGrade(null);
  };

  const cancelGeneration = () => {
    resetGeneration();
  };

  const startGeneration = () => {
    // Orchestrate generation: fetch chapter chunks and call Gemini from the browser.
    if (isGenerating) return;
    setModalError(null);

    // basic validation
    if (!modalGrade) { setModalError('Please select a grade'); return; }
    if (!modalSubject || modalSubject.trim() === '') { setModalError('Please select or enter a subject'); return; }
    if (!modalExamName || modalExamName.trim() === '') { setModalError('Please enter a name for the exam'); return; }
    const minutes = Number(modalExamTime);
    if (!modalExamTime || Number.isNaN(minutes) || minutes <= 0) { setModalError('Please enter a valid time (in minutes)'); return; }
    if (!modalSelectedGrades || modalSelectedGrades.length === 0) { setModalError('Please select at least one grade content to include in the exam'); return; }

    // collect chapters that have a positive assigned count
    const chapterIdsWithCounts = Object.entries(modalChapterCounts || {}).filter(([k, v]) => Number(v) > 0).map(([k]) => Number(k));
    if (chapterIdsWithCounts.length === 0) { setModalError('No chapters have questions assigned'); return; }

    // initialize progress UI
    const initialChapterProgress = chapterIdsWithCounts.map((cid) => {
      const chapObj = Object.values(modalChaptersByGrade).flat().find((c: any) => c.id === cid) || { id: cid, chapter_name: `Chapter ${cid}` };
      // prefer several possible name fields returned by backend
      const chapterTitle = chapObj.chapter_name ?? chapObj.title ?? chapObj.name ?? chapObj.chapter_title ?? `Chapter ${cid}`;
      return { chapterId: cid, title: chapterTitle, total: modalChapterCounts[cid] || 0, completed: 0, status: 'pending' as const };
    });
    setChapterProgress(initialChapterProgress);

  // initialize overall progress
  setOverallCompletedCount(0);
  setOverallTotalCount(initialChapterProgress.length);
  setCurrentChapterStatusText('Preparing generation...');

  // move modal to generation/progress view
  setIsGenerating(true);
  setProgressStep(5);

    // fetch user gemini key and run generation sequentially per-chapter
    (async () => {
      try {
        const headers: any = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        let key: string | null = null
        try {
          const d = await request('/api/user/gemini-key/', { method: 'GET', headers })
          if (d?.has_key && d?.api_key) key = String(d.api_key)
        } catch (e) {
          // ignore, handle below
        }
        if (!key) {
          setModalError('Gemini API key not available. Please save your Gemini key in your profile.');
          setIsGenerating(false);
          return;
        }
        setGeminiKey(key);

        const ai = new GoogleGenAI({ apiKey: key.trim() });
        const allGenerated: any[] = [];

        for (let idx = 0; idx < initialChapterProgress.length; idx++) {
          const ch = initialChapterProgress[idx];
          // update status
          setChapterProgress((prev) => prev.map((p) => p.chapterId === ch.chapterId ? { ...p, status: 'running' } : p));
          setCurrentChapterStatusText(`Generating: ${ch.title}`);

            try {
            // fetch chunks for chapter
            let chunkData: any = null
            try {
              chunkData = await request(`/api/textbook/chapter/by-api/${ch.chapterId}/chunks/`, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
            } catch (err) {
              throw new Error('Failed to fetch chapter chunks')
            }
            const chunks = Array.isArray(chunkData?.chunks) ? chunkData.chunks : [];
            // build an array of { page, text } to send to the model so references can include page numbers
            const chunksPayload = chunks.map((c: any) => ({
              page: c.page_number ?? c.page ?? c.page_no ?? c.p ?? null,
              text: String(c.text_content ?? c.content ?? c.text ?? '').trim()
            })).filter((x: any) => x.text && x.text.length > 0);
            const chunksJson = JSON.stringify(chunksPayload);
            const chapterTitle = ch.title ?? `Chapter ${ch.chapterId}`;
            const chapterMetaJson = JSON.stringify({ title: chapterTitle });
            console.log('Chunks JSON for chapter', ch, chunksJson);

            // build a detailed prompt with explicit rules requested by the user
            const promptLines: string[] = [];
            // difficulty instruction: respect the selected difficultyMode and difficultyPercents
            let difficultyInstruction = '';
            if (difficultyMode === 'mixed') {
              difficultyInstruction = `Difficulty distribution: generate approximately ${difficultyPercents.easy}% easy, ${difficultyPercents.medium}% medium and ${difficultyPercents.hard}% hard questions.`;
            } else if (difficultyMode === 'custom') {
              difficultyInstruction = `Difficulty distribution (custom): generate approximately ${difficultyPercents.easy}% easy, ${difficultyPercents.medium}% medium and ${difficultyPercents.hard}% hard questions.`;
            } else {
              // easy | medium | hard
              difficultyInstruction = `Difficulty: generate questions at the '${difficultyMode}' difficulty level.`;
            }
            promptLines.push('You are an expert question writer. Use ONLY the provided CHAPTER_META_JSON and SOURCE_CHUNKS_JSON as authoritative factual sources. Do NOT invent facts or add information not present in those inputs. When writing questions and explanations, AVOID any phrasing that signals the passage origin (do NOT use: "based on the text", "from the passage", "according to the snippet", "from the text above", etc.). Questions and choices should read as standalone exam items.');
            promptLines.push('');
            promptLines.push('Output format (VERY STRICT):');
            promptLines.push(`1) Return EXACTLY a JSON array with ${ch.total} objects and nothing else.`);
            promptLines.push('2) Each object MUST contain ONLY these keys: question, choice_a, choice_b, choice_c, choice_d, ans, explanation, reference.');
            promptLines.push('   - "ans" must be one of the letters: "A", "B", "C", or "D".');
            promptLines.push('');
            promptLines.push('Question/choice rules:');
            // include difficulty instruction so the model knows target difficulty
            promptLines.push(difficultyInstruction);
            promptLines.push('- Produce clear, unambiguous multiple-choice questions. Choices should be concise. Do NOT include choice labels like "A)" inside the option strings.');
            promptLines.push('- If a question or any choice requires a mathematical expression, use LaTeX notation. Wrap inline math with "$...$" and display math with "$$...$$".');
            promptLines.push('- Do NOT mention that the question was generated from a provided passage; make the item read like a regular exam question.');
            promptLines.push('');
            promptLines.push('Explanation rules:');
            promptLines.push('- The "explanation" field must be an elaborative, pedagogical explanation that justifies the correct answer and briefly explains why the other options are incorrect.');
            promptLines.push('- Explanations should be standalone teaching text (not simply "see the text"). They should not exclusively cite the provided chunks; instead, explain the concept clearly.');
            promptLines.push('- The "explanation" may include minimal HTML for structure (e.g., <p>, <ol>, <li>, <strong>) and LaTeX for formulas. Keep HTML well-formed and minimal.');
            promptLines.push('');
            promptLines.push('Reference rules:');
            promptLines.push('- The "reference" field MUST begin with exactly:');
            promptLines.push(`  "Grade ${modalGrade} - ${chapterTitle} - Page # <PAGE_NUMBER> - "`);
            promptLines.push('- followed by a short quoted excerpt (in double quotes) taken from the chunk with that page number in SOURCE_CHUNKS_JSON. DO NOT invent page numbers; use only the "page" values provided in SOURCE_CHUNKS_JSON. If the supporting excerpt spans multiple pages, include only the first page.');
            promptLines.push('');
            promptLines.push('Model behavior notes:');
            promptLines.push('- Keep the "question" and "choice_*" fields plain (no HTML) except LaTeX for math. The "explanation" may contain simple HTML + LaTeX as described.');
            promptLines.push('- Be precise, concise in the question; be pedagogical and slightly more elaborate in explanations (2-6 short paragraphs or a short ordered list).');
            promptLines.push('');
            promptLines.push('CHAPTER_META_JSON:');
            promptLines.push(chapterMetaJson);
            promptLines.push('');
            promptLines.push('SOURCE_CHUNKS_JSON:');
            promptLines.push(chunksJson);
            promptLines.push('');
            promptLines.push('Respond with JSON only.');
            const prompt = promptLines.join('\n');

            const genResp = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: prompt });

            // parse model response into text
            const parseModelText = (r: any) => {
              if (!r) return null;
              if (typeof r === 'string') return r;
              if (typeof r.text === 'string') return r.text;
              if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content;
              if (Array.isArray(r.outputs) && r.outputs.length) {
                const out0 = r.outputs[0];
                if (typeof out0 === 'string') return out0;
                if (out0?.content && typeof out0.content === 'object') {
                  if (typeof out0.content?.text === 'string') return out0.content.text;
                  if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text;
                }
              }
              return null;
            };

            const outText = parseModelText(genResp);
            let parsed: any[] = [];
            if (outText) {
              try {
                const start = outText.indexOf('[');
                const end = outText.lastIndexOf(']');
                const jsonStr = start >= 0 && end > start ? outText.slice(start, end + 1) : outText;
                parsed = JSON.parse(jsonStr);
              } catch (pe) {
                console.warn('Failed to parse Gemini output for chapter', ch.chapterId, pe);
                parsed = [];
              }
            }

            const normalized = (parsed && Array.isArray(parsed)) ? parsed.map((it: any) => ({
              chapter_id: ch.chapterId,
              chapter_title: ch.title,
              question: it.question || '',
              choice_a: it.choice_a || it.choiceA || it.a || '',
              choice_b: it.choice_b || it.choiceB || it.b || '',
              choice_c: it.choice_c || it.choiceC || it.c || '',
              choice_d: it.choice_d || it.choiceD || it.d || '',
              ans: (it.ans || it.answer || '').toString(),
              explanation: it.explanation || it.explain || '',
              reference: it.reference || it.ref || ''
            })) : [];

            allGenerated.push(...normalized);
            setChapterProgress((prev) => prev.map((p) => p.chapterId === ch.chapterId ? { ...p, completed: ch.total, status: 'done' } : p));
            // update overall completed count
            setOverallCompletedCount((c) => c + 1);
            setCurrentChapterStatusText(`Completed: ${ch.title}`);
          } catch (ce) {
            console.error('Chapter generation failed', ce);
            setChapterProgress((prev) => prev.map((p) => p.chapterId === ch.chapterId ? { ...p, status: 'error' } : p));
            setOverallCompletedCount((c) => c + 1);
            setCurrentChapterStatusText(`Error generating: ${ch.title}`);
          }
        }

        // done
        setGeneratedQuestions(allGenerated);
        setIsGenerating(false);
        // move to Review tab
        setProgressStep(6);
        setModalStep(7);
        setCurrentChapterStatusText('Generation complete');
        // prepare overall progress to show final state (all chapters completed)
        setOverallCompletedCount(initialChapterProgress.length);
      } catch (err) {
        console.error('Generation orchestration failed', err);
  setModalError('Generation failed: ' + ((err as any)?.message ?? ''));
        setIsGenerating(false);
      }
    })();
  };

  // helper sanitize + optionally render LaTeX placeholder (keeps simple)
  const sanitizeHtml = (html: string) => {
    try {
      return DOMPurify.sanitize(String(html || ''));
    } catch (e) {
      return String(html || '');
    }
  };

  // Render LaTeX expressions inside an HTML string using KaTeX, then sanitize.
  const renderLatexInHtml = (html: string) => {
    if (!html) return '';
    try {
      let out = String(html);
      out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => {
        try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }); } catch (e) { return _m; }
      });
      out = out.replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => {
        try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }); } catch (e) { return _m; }
      });
      try { return DOMPurify.sanitize(out); } catch (e) { return out; }
    } catch (e) {
      try { return DOMPurify.sanitize(String(html)); } catch (err) { return String(html); }
    }
  };

  const saveGeneratedExam = async () => {
    setSaveError(null);
    setSaveSuccess(null);
    if (!modalExamName || !modalSubject || !modalGrade) {
      setSaveError('Please ensure exam name, grade and subject are set before saving.');
      return;
    }
    if (!generatedQuestions || generatedQuestions.length === 0) {
      setSaveError('No generated questions to save');
      return;
    }
    setSaveLoading(true);
    try {
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // create exam
      const examPayload: any = {
        name: modalExamName,
        price: 0,
        time: Number(modalExamTime) || undefined,
        subject: modalSubject,
        grade: modalGrade,
        exam_scope: 'none',
        national_exam: nationalSelectedId || undefined,
        is_generated: true,
      };
      const createData = await request('/api/school/add-exam/', { method: 'POST', headers, body: JSON.stringify(examPayload) });
      const examId = createData?.id;
      if (!examId) throw new Error('Server did not return exam id');

      // prepare questions payload for server
      const payloadQuestions = generatedQuestions.map((q: any) => {
        // prefer numeric chapter id; backend expects numeric or null
        const rawChap = q.chapter_id ?? q.chapter ?? null;
        const chap = rawChap === null || rawChap === undefined ? null : (Number(rawChap) && !Number.isNaN(Number(rawChap)) ? Number(rawChap) : null);
        return {
          // send question text under 'ques' to avoid backend ambiguity where 'question' is
          // sometimes interpreted as an id for updates
          ques: q.question || q.ques || q.question_text || '',
          choice_a: q.choice_a || q.choiceA || q.a || '',
          choice_b: q.choice_b || q.choiceB || q.b || '',
          choice_c: q.choice_c || q.choiceC || q.c || '',
          choice_d: q.choice_d || q.choiceD || q.d || '',
          ans: (q.ans || q.answer || '').toString(),
          explanation: q.explanation || '',
          reference: q.reference || '',
          chapter: chap,
        };
      });

      await request(`/api/school/exam-detail/${examId}/add-questions/`, { method: 'POST', headers, body: JSON.stringify(payloadQuestions) });

      setSaveSuccess('Exam and questions saved successfully');
      // mark modal state or refresh list as needed: clear generated questions and close modal
      setGeneratedQuestions([]);
      // close modal and go to exam detail page for the created exam
      setShowGenerateModal(false);
      try {
        navigate(`/exams/exam-detail/${examId}`);
      } catch (e) {
        // ignore navigation errors
      }
    } catch (e: any) {
      console.error('Save generated exam failed', e);
      setSaveError(e?.message || 'Save failed');
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  // when modalGrade changes, fetch grade-specific subjects from backend
  useEffect(() => {
    let mounted = true;
    if (!modalGrade) {
      setModalSubjects([]);
      setModalSubject('');
      setModalSubjectsLoading(false);
      return;
    }
    const t = token ?? localStorage.getItem('token');
    (async () => {
      setModalSubjectsLoading(true);
      try {
        const data = await getGradeSubjects(t ?? null, String(modalGrade));
        if (!mounted) return;
        // backend may return { subjects: [...] } or { results: [...] } or an array
        let arr: any[] = [];
        if (Array.isArray(data)) arr = data;
        else if (Array.isArray(data?.subjects)) arr = data.subjects;
        else if (Array.isArray(data?.results)) arr = data.results;

        // try to get user's institution id to filter subjects to this school
        let instId: number | null = null;
        try {
          const headers: any = { 'Content-Type': 'application/json' };
          if (t) headers['Authorization'] = `Bearer ${t}`;
          const udata = await request('/api/user-info/', { headers });
          instId = udata?.institution_id ?? null;
        } catch (e) {
          instId = null;
        }

        // normalize and filter by institution membership if possible
        const mapped = (arr || [])
          .filter((s: any) => {
            if (!instId) return true;
            const insts = Array.isArray(s.institutions) ? s.institutions : (Array.isArray(s?.institutions?.results) ? s.institutions.results : []);
            return insts.length === 0 ? true : insts.includes(instId);
          })
          .map((s: any) => ({ id: s.id ?? s.subject_id ?? 0, name: s.subject_name ?? s.name ?? String(s.id ?? '') }));

        setModalSubjects(mapped || []);
        setModalSubjectsLoading(false);
      } catch (err) {
        if (!mounted) return;
        setModalSubjects([]);
        setModalSubjectsLoading(false);
        setModalError('Failed to load subjects for selected grade');
      }
    })();
    return () => { mounted = false; };
  }, [modalGrade, token]);

  // when user navigates to allocation step (modalStep 4), fetch chapters for each selected grade filtered by subject
  useEffect(() => {
    let mounted = true;
    if (modalStep !== 4) return;
    if (!modalSubject) {
      setModalChaptersByGrade({});
      return;
    }
    // Do not request school subject chapters when running in university/college mode
    if (role === 'college_university') {
      setModalChaptersByGrade({});
      return;
    }
    const t = token ?? localStorage.getItem('token');
    (async () => {
      const map: Record<number, any[]> = {};
      const counts: Record<number, number> = { ...(modalChapterCounts || {}) };
          for (const gid of modalSelectedGrades) {
        try {
          const qp = new URLSearchParams();
          qp.set('grade_id', String(gid));
          const url = `/api/school/subjects/${modalSubject}/chapters/?${qp.toString()}`;
              let data: any = null
              try {
                data = await request(url, { headers: { Authorization: t ? `Bearer ${t}` : '' } })
              } catch (e) {
                map[gid] = [];
                continue;
              }
          const chapters = data?.chapters || [];
          // filter chapters to those that include this grade
          const filtered = chapters.filter((c: any) => {
            try {
              const gradeArr = Array.isArray(c.school_grades) && c.school_grades.length ? c.school_grades : (Array.isArray(c.school_grade_ids) ? c.school_grade_ids : []);
              if (!Array.isArray(gradeArr) || gradeArr.length === 0) return false;
              return gradeArr.map((x: any) => Number(x)).includes(Number(gid));
            } catch (e) {
              return false;
            }
          });
          map[gid] = filtered;
          // init counts
          filtered.forEach((c: any) => { if (counts[c.id] == null) counts[c.id] = 0; });
        } catch (e) {
          map[gid] = [];
        }
      }
      if (!mounted) return;
      setModalChaptersByGrade(map);
      setModalChapterCounts(counts);
    })();
    return () => { mounted = false; };
  }, [modalStep, modalSelectedGrades, modalSubject, token, role]);

  // when modalSubject changes, determine which grades (for this school) have that subject
  useEffect(() => {
    let mounted = true;
    if (!modalSubject) {
      setModalGradesWithSubject(null);
      return;
    }
    // Avoid fetching subject chapters for university admins
    if (role === 'college_university') {
      setModalGradesWithSubject([]);
      return;
    }
    const t = token ?? localStorage.getItem('token');
    (async () => {
      try {
        const headers: any = { 'Content-Type': 'application/json' };
        if (t) headers['Authorization'] = `Bearer ${t}`;
        // call chapters endpoint for the subject without grade filter to get chapters across grades
        const url = `/api/school/subjects/${modalSubject}/chapters/`;
        let data: any = null
        try {
          data = await request(url, { headers })
        } catch (e) {
          if (mounted) setModalGradesWithSubject([])
          return
        }
        const chapters = data?.chapters || [];
        const gradeIdSet = new Set<number>();
        chapters.forEach((c: any) => {
          const gradeArr = Array.isArray(c.school_grades) && c.school_grades.length ? c.school_grades : (Array.isArray(c.school_grade_ids) ? c.school_grade_ids : []);
          if (Array.isArray(gradeArr)) {
            gradeArr.forEach((g: any) => {
              const num = Number(g);
              if (!Number.isNaN(num)) gradeIdSet.add(num);
            });
          }
        });
        if (!mounted) return;
        setModalGradesWithSubject(Array.from(gradeIdSet));
        // also remove any selected grades that are no longer available
        setModalSelectedGrades((prev) => prev.filter((id) => gradeIdSet.size === 0 ? true : gradeIdSet.has(id)));
      } catch (e) {
        if (!mounted) return;
        setModalGradesWithSubject([]);
      }
    })();
    return () => { mounted = false; };
  }, [modalSubject, token, role]);

  // keep active grade tab in sync with selected grades (default to first selected)
  useEffect(() => {
    if (!modalSelectedGrades || modalSelectedGrades.length === 0) {
      setModalActiveGrade(null);
      return;
    }
    // if current active is not in selected list, set to first
    if (modalActiveGrade == null || !modalSelectedGrades.includes(modalActiveGrade)) {
      setModalActiveGrade(modalSelectedGrades[0]);
    }
  }, [modalSelectedGrades]);

  // hook to fetch national exams for the picker when needed
  const { data: nationalExams, isLoading: nationalExamsLoading } = useNationalExams('school', !!showNationalPicker);
  const [nationalAggregateLoading, setNationalAggregateLoading] = useState(false);
  const [nationalAggregateError, setNationalAggregateError] = useState<string | null>(null);

  const applySelectedNationalExam = async () => {
    setNationalAggregateError(null);
    if (!nationalSelectedId) return;
    if (!modalSubject) {
      setNationalAggregateError('Please select a subject first');
      return;
    }
    try {
      setNationalAggregateLoading(true);
      // call backend aggregate endpoint
      const url = `/api/exam/national/${nationalSelectedId}/aggregate/${modalSubject}/`;
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const data = await request(url, { method: 'GET', headers });
      // expected shape: { total: number, chapters: [{ id, name, avg }], exams_count }
      const total = Number(data.total) || 0;
      setModalTotalQuestions(String(total));
      // build new counts map, only for chapters that are present in current modalChaptersByGrade
      const allChapters = modalSelectedGrades.flatMap((gid) => modalChaptersByGrade[gid] || []);
      const availableIds = new Set(allChapters.map((c: any) => c.id));
      const newCounts: Record<number, number> = { ...(modalChapterCounts || {}) };
      if (Array.isArray(data.chapters)) {
        data.chapters.forEach((ch: any) => {
          const id = Number(ch.id);
          const avg = Math.max(0, Math.round(Number(ch.avg) || 0));
          if (availableIds.has(id)) {
            newCounts[id] = avg;
          }
        });
      }
      // If there are available chapters but aggregate returned none for some, try to distribute remainder
      const chapIds = Array.from(availableIds);
      const sumAssigned = Object.values(newCounts || {}).reduce((s, v) => s + (Number(v) || 0), 0);
      if (chapIds.length > 0 && sumAssigned !== total) {
        // distribute difference (could be positive or negative) across chapters
        let diff = total - sumAssigned;
        const baseAdjust = Math.floor(diff / chapIds.length);
        chapIds.forEach((id: any, idx: number) => {
          newCounts[id] = (newCounts[id] || 0) + baseAdjust;
        });
        diff = total - Object.values(newCounts).reduce((s, v) => s + (Number(v) || 0), 0);
        let i = 0;
        while (diff > 0) {
          const id = chapIds[i % chapIds.length];
          newCounts[id] = (newCounts[id] || 0) + 1;
          diff -= 1;
          i += 1;
        }
        while (diff < 0) {
          const id = chapIds[i % chapIds.length];
          newCounts[id] = Math.max(0, (newCounts[id] || 0) - 1);
          diff += 1;
          i += 1;
        }
      }
      setModalChapterCounts(newCounts);
      setShowNationalPicker(false);
    } catch (e: any) {
      setNationalAggregateError(e?.message || 'Failed to apply national exam aggregate');
    } finally {
      setNationalAggregateLoading(false);
    }
  };

  const displayedExams = exams.filter((e) => {
    // filter by selected grade (prefer numeric school_grade_id if available)
    if (nowGrade && nowGrade !== 0) {
      if (typeof e.school_grade_id === 'number') {
        if (e.school_grade_id !== nowGrade) return false;
      } else {
        // fallback to comparing grade name
        const gradeObj = gradeData?.grades?.find((g: any) => g.id === nowGrade);
        if (gradeObj && (e.grade || '').toString() !== gradeObj.grade_name?.toString()) return false;
      }
    }
    // filter by selected subject id if set
    if (nowSubject && nowSubject !== 0) {
      if (typeof e.subject_id === 'number') {
        if (e.subject_id !== nowSubject) return false;
      } else {
        // fallback: compare subject name
        if (e.sub && subjects.find(s => s.id === nowSubject && s.name !== e.sub)) return false;
      }
    }
    return true;
  });

  const isAllowed = !!token && (role === 'superuser' || role === 'school' || role === 'college_university');

  return (
    <div className="grid grid-cols-12 gap-5">
      <div className="col-span-3">
        <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
          <div className="max-w-full overflow-x-auto">
            <PageTitle title="Generate Exam" />
            <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">Grades</h4>
          </div>
          {
            !isAllowed ? (
              <p className="text-red-600">403 - Not authorized</p>
            ) : gradeData?.grades == null ? <p>Error</p>
              : gradeData.grades.length === 0 ? <p>No grades</p>
                : gradeData.grades.map((grade: { id: number, grade_name: string }, key: number) => (
                  <ListGroup.Item key={key} className={`mb-2 cursor-pointer rounded-lg p-3 transition-all duration-200 ease-in-out list-none ${nowGrade == grade.id ? 'bg-gray-200 dark:bg-gray-600 text-blue-600 dark:text-blue-400' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`} onClick={() => { setNowGrade(grade.id); }} active={nowGrade == grade.id ? true : false}>
                    <span className="text-lg font-medium">Grade {grade.grade_name}</span>
                  </ListGroup.Item>
                ))
          }
        </div>
      </div>

      <div className="col-span-9">
        <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
          <div className="max-w-full overflow-x-auto">
            <div className="mb-6 flex items-center justify-between">
              <h4 className="text-xl font-semibold text-black dark:text-white">Generated Exams</h4>
              {isAllowed && (
                <div className="flex items-center">
                  {/* Subject filter */}
                  <div className="mr-4">
                    <select aria-label="Filter by subject" value={nowSubject ?? 0} onChange={(e) => setNowSubject(Number(e.target.value))} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                      <option value={0}>All subjects</option>
                      {subjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                    </select>
                  </div>
                  <button
                    onClick={() => { setShowGenerateModal(true); resetGeneration(); setModalGrade(null); setModalSubject(''); setModalError(null); }}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                  >
                    Generate exam
                  </button>
                </div>
              )}
            </div>

            {loading && <div>Loading generated exams...</div>}
            {error && <div className="text-red-600">{error}</div>}

            {!loading && !error && (
              <Table hoverable className="exam-table">
                <Table.Head>
                  <Table.HeadCell>EXAM NAME</Table.HeadCell>
                  <Table.HeadCell>SUBJECT / GRADE</Table.HeadCell>
                  <Table.HeadCell>No OF QUESTIONS</Table.HeadCell>
                  <Table.HeadCell>TIME(IN MINUTES)</Table.HeadCell>
                  <Table.HeadCell>STATUS</Table.HeadCell>
                  <Table.HeadCell>ACTIONS</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {displayedExams.length === 0 && (
                    <Table.Row>
                      <Table.Cell className="px-4 py-6 text-center text-sm text-bodydark2" colSpan={6}>No generated exams found for your school.</Table.Cell>
                    </Table.Row>
                  )}
                  {displayedExams.map((ex) => (
                    <Table.Row key={ex.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                      <Table.Cell className="whitespace-nowrap font-bold text-slate-600 dark:text-white">{ex.name}</Table.Cell>
                      <Table.Cell>
                        <div className="font-bold">
                          {ex.sub ? ex.sub : ''}
                          {(() => {
                            // prefer numeric school_grade_id -> map to grade name from gradeData
                            const gid = (ex as any).school_grade_id ?? (ex as any).grade ?? null;
                            if (typeof (ex as any).school_grade_id === 'number' && gradeData?.grades) {
                              const g = gradeData.grades.find((gg: any) => gg.id === (ex as any).school_grade_id);
                              return g ? ` / ${g.grade_name}` : (ex.grade ? ` / ${ex.grade}` : '');
                            }
                            // fallback: ex.grade may already be a name
                            return ex.grade ? ` / ${ex.grade}` : '';
                          })()}
                        </div>
                      </Table.Cell>
                      <Table.Cell>{ex.no_ques}</Table.Cell>
                      <Table.Cell>{ex.time_given ? `${ex.time_given} minutes` : 'Not assigned'}</Table.Cell>
                      <Table.Cell>{ex.status}</Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <button
                            aria-label={`View exam ${ex.name}`}
                            onClick={() => navigate(`/exams/exam-detail/${ex.id}`)}
                            className="inline-flex items-center justify-center rounded-md p-2 text-sm text-gray-600 hover:bg-gray-100"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

              {/* Generate progress modal */}
              {showGenerateModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
                  <div className="relative z-[10000] w-full max-w-3xl rounded-lg bg-white p-6 shadow-lg dark:bg-boxdark">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold">Generating Exam</h3>
                      <button onClick={() => { cancelGeneration(); setShowGenerateModal(false); }} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
                    </div>

                    <div className="mt-4">
                      {/* Progress bar top (styled to match attached image) */}
                      <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
                        {/* Grid-based progress: 11 columns (circle, connector, circle, ... for 6 steps) */}
                        <div className="w-full">
                          <div className="flex items-center w-full">
                            {/* render circle, connector, circle... */}
                            {(() => {
                              const steps = [
                                { label: 'Select', desc: '' },
                                { label: 'Details', desc: '' },
                                { label: 'Contents', desc: '' },
                                { label: 'Allocate', desc: '' },
                                { label: 'Difficulty', desc: '' },
                                { label: 'Generating', desc: '' },
                                { label: 'Review', desc: '' },
                              ];
                              const nodes: JSX.Element[] = [];
                              steps.forEach((_, idx) => {
                                const completed = progressStep > idx;
                                const current = progressStep === idx;
                                // circle cell
                                nodes.push(
                                  <div key={`circle-${idx}`} className="flex-none flex justify-center">
                                    <div className="relative z-20">
                                      <div className={`flex h-8 w-8 items-center justify-center rounded-full transition-all ${completed ? 'bg-blue-600' : current ? 'bg-blue-200' : 'bg-white border border-gray-200'}`}>
                                        {completed ? (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
                                          </svg>
                                        ) : (
                                          <div className={`h-2 w-2 rounded-full ${current ? 'bg-blue-600' : 'bg-transparent'}`} />
                                        )}
                                      </div>
                                      <div className={`absolute -inset-1 rounded-full ${completed ? 'ring-2 ring-blue-200' : current ? 'ring-1 ring-blue-100' : ''}`} />
                                    </div>
                                  </div>
                                );
                                // connector cell (except after last)
                                if (idx < steps.length - 1) {
                                  nodes.push(
                                    // add horizontal padding so connector doesn't reach into circles
                                    <div key={`conn-${idx}`} className="flex-1 flex items-center px-3 relative z-0">
                                      <div className={`h-2 w-full rounded-full transition-colors ${progressStep > idx ? 'bg-blue-600' : 'bg-gray-200'}`} />
                                    </div>
                                  );
                                }
                              });
                              return nodes;
                            })()}
                          </div>

                          {/* labels row: use same 7-column grid, put labels under circle columns */}
                          <div className="mt-3 w-full">
                            <div className="flex items-start w-full">
                              {(() => {
                                const steps = [
                                  { label: 'Select', desc: '' },
                                  { label: 'Details', desc: '' },
                                  { label: 'Contents', desc: '' },
                                  { label: 'Allocate', desc: '' },
                                  { label: 'Difficulty', desc: '' },
                                  { label: 'Generating', desc: '' },
                                  { label: 'Review', desc: '' },
                                ];
                                const nodes: JSX.Element[] = [];
                                steps.forEach((item, idx) => {
                                  // place the label directly under the circle and center it
                                  nodes.push(
                                    <div key={`label-${idx}`} className="flex-none flex justify-center w-8">
                                      <div className="text-xs font-semibold text-gray-700">{item.label}</div>
                                    </div>
                                  );
                                  if (idx < steps.length - 1) {
                                    nodes.push(<div key={`label-gap-${idx}`} className="flex-1" />);
                                  }
                                });
                                return nodes;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Step 1: grade/subject selection */}
                      {modalStep === 1 && (
                        <div className="mb-4 grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="modal-grade" className="mb-2 block text-sm font-medium text-gray-700">Grade</label>
                            <select id="modal-grade" value={modalGrade ?? ''} onChange={(e) => setModalGrade(e.target.value ? Number(e.target.value) : null)} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm">
                              <option value="">Select grade</option>
                              {gradeData?.grades?.map((g: any) => (
                                <option key={g.id} value={g.id}>Grade {g.grade_name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="modal-subject" className="mb-2 block text-sm font-medium text-gray-700">Subject</label>
                            <select
                              id="modal-subject"
                              value={modalSubject}
                              onChange={(e) => setModalSubject(e.target.value)}
                              disabled={!modalGrade || modalSubjectsLoading}
                              className={`w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm ${!modalGrade ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            >
                              {!modalGrade && <option value="">Select grade first</option>}
                              {modalGrade && <option value="">Not selected</option>}
                              {modalGrade && modalSubjectsLoading && <option value="">Loading subjects...</option>}
                              {modalGrade && !modalSubjectsLoading && modalSubjects.length === 0 && <option value="">No subjects for selected grade</option>}
                              {modalGrade && !modalSubjectsLoading && modalSubjects.map((s) => (
                                <option key={s.id} value={String(s.id)}>{s.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      {modalStep === 2 && (
                        <div className="mb-4 grid grid-cols-1 gap-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="exam-name" className="mb-2 block text-sm font-medium text-gray-700">Exam Name</label>
                              <input id="exam-name" value={modalExamName} onChange={(e) => setModalExamName(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm" placeholder="E.g. Midterm - Term 1" />
                            </div>
                            <div>
                              <label htmlFor="exam-time" className="mb-2 block text-sm font-medium text-gray-700">Time (minutes)</label>
                              <input id="exam-time" value={modalExamTime} onChange={(e) => setModalExamTime(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm" placeholder="Enter duration in minutes" inputMode="numeric" />
                            </div>
                          </div>
                          <div>
                            <label htmlFor="exam-desc" className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                            <textarea id="exam-desc" value={modalExamDescription} onChange={(e) => setModalExamDescription(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm" placeholder="Optional description" rows={4} />
                          </div>
                        </div>
                      )}

                      {/* Step 3: select which grade contents to include (multi-select pills) */}
                      {modalStep === 3 && (
                        <div className="mb-4">
                          <label className="mb-3 block text-sm font-medium text-gray-700">Include contents from grades</label>
                          <div className="flex flex-wrap">
                            {!modalSubject ? (
                              <div className="text-sm text-gray-500">Select a subject first to see available grades.</div>
                            ) : modalGradesWithSubject === null ? (
                              <div className="text-sm text-gray-500">Loading available grades...</div>
                            ) : (() => {
                              // show only grades that have this subject for the current institute
                              const gradesToShow = (gradeData?.grades || []).filter((g: any) => modalGradesWithSubject && modalGradesWithSubject.includes(g.id));
                              if (!gradesToShow || gradesToShow.length === 0) return <div className="text-sm text-gray-500">No grades available for the selected subject in your school.</div>;
                              return gradesToShow.map((g: any) => {
                                const selected = modalSelectedGrades.includes(g.id);
                                return (
                                  <button
                                    key={g.id}
                                    type="button"
                                    onClick={() => {
                                      setModalError(null);
                                      setModalSelectedGrades((prev) => selected ? prev.filter((id) => id !== g.id) : [...prev, g.id]);
                                    }}
                                    className={`mr-2 mb-2 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                                  >
                                    {selected && (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                    <span className="whitespace-nowrap">Grade {g.grade_name}</span>
                                  </button>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Step 4: total questions and per-chapter allocation for selected grades */}
                      {modalStep === 4 && (
                        <div className="mb-4">
                          <div className="mb-4 w-full max-w-lg">
                            <label className="mb-2 block text-sm font-medium text-gray-700">Total number of questions</label>
                            <div className="flex items-center gap-3">
                              <input
                                type="number"
                                min={1}
                                value={modalTotalQuestions}
                                onChange={(e) => setModalTotalQuestions(e.target.value)}
                                aria-label="Total questions"
                                placeholder="e.g. 50"
                                className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm"
                              />
                              <button
                                type="button"
                                onClick={() => { setShowNationalPicker(true); setNationalSelectedId(null); }}
                                className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
                                aria-label="Mimick national exam"
                              >
                                mimick national exam
                              </button>
                            </div>
                          </div>
                          {/* national exam picker modal (small overlay) */}
                          {showNationalPicker && (
                            <div className="absolute left-0 top-0 z-[11000] flex h-full w-full items-center justify-center bg-black/30">
                              <div className="w-full max-w-md rounded-md bg-white p-4 shadow-lg">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="text-lg font-semibold">Select national exam</div>
                                  <button onClick={() => setShowNationalPicker(false)} className="text-sm text-gray-600">Close</button>
                                </div>
                                <div className="max-h-64 overflow-auto">
                                  {nationalExamsLoading && <div className="text-sm text-gray-500">Loading...</div>}
                                  {!nationalExamsLoading && (!nationalExams || nationalExams.length === 0) && (
                                    <div className="text-sm text-gray-500">No national exams available</div>
                                  )}
                                  {!nationalExamsLoading && nationalExams && nationalExams.map((n: any) => (
                                    <label key={n.id} className="flex items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                                      <input type="radio" name="nationalExam" value={n.id} checked={nationalSelectedId === n.id} onChange={() => setNationalSelectedId(n.id)} />
                                      <div>
                                        <div className="text-sm font-medium">{n.name}</div>
                                        <div className="text-xs text-gray-500">{n.creator_name ?? 'National'}</div>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                                {nationalAggregateError && <div className="mt-2 text-sm text-red-600">{nationalAggregateError}</div>}
                                <div className="mt-4 flex justify-end gap-2">
                                  <button onClick={() => setShowNationalPicker(false)} className={`rounded-md bg-gray-200 px-3 py-1 text-sm ${nationalAggregateLoading ? 'opacity-60 pointer-events-none' : ''}`}>Cancel</button>
                                  <button onClick={applySelectedNationalExam} className={`rounded-md px-3 py-1 text-sm text-white ${nationalSelectedId && !nationalAggregateLoading ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`} disabled={!nationalSelectedId || nationalAggregateLoading}>{nationalAggregateLoading ? 'Applying...' : 'Use'}</button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* grade tabs header */}
                          <div className="mb-4">
                            {modalSelectedGrades.length === 0 && <div className="text-sm text-gray-500">No grades selected.</div>}
                            {modalSelectedGrades.length > 0 && (
                              <div>
                                <div className="mb-3 flex space-x-2 overflow-x-auto">
                                  {modalSelectedGrades.map((gid) => {
                                    const gradeObj = gradeData?.grades?.find((g: any) => g.id === gid);
                                    const active = modalActiveGrade === gid;
                                    return (
                                      <button
                                        key={gid}
                                        type="button"
                                        onClick={() => setModalActiveGrade(gid)}
                                        className={`px-4 py-2 rounded-t-md border-b-2 ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-transparent bg-white text-gray-700 hover:bg-gray-50'}`}
                                      >
                                        Grade {gradeObj?.grade_name ?? gid}
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* active grade chapters */}
                                <div className="p-3 border rounded-b-md bg-white">
                                  {modalActiveGrade == null ? (
                                    <div className="text-sm text-gray-500">Select a grade to view chapters.</div>
                                  ) : (() => {
                                    const chapters = modalChaptersByGrade[modalActiveGrade] || [];
                                    const gradeObj = gradeData?.grades?.find((g: any) => g.id === modalActiveGrade);
                                    return (
                                      <div>
                                        <div className="mb-2 text-sm font-semibold">Grade {gradeObj?.grade_name ?? modalActiveGrade}</div>
                                        {chapters.length === 0 ? (
                                          <div className="text-sm text-gray-500">No chapters available for this grade & subject.</div>
                                        ) : (
                                          <div className="space-y-2">
                                            {chapters.map((c: any) => (
                                              <div key={c.id} className="flex items-center gap-4">
                                                <div className="flex-1 text-sm">{c.chapter_name ?? c.name ?? c.title ?? `Chapter ${c.id}`}</div>
                                                <div className="w-40">
                                                  <input
                                                    type="number"
                                                    min={0}
                                                    aria-label={`Questions for ${c.chapter_name || c.name || ('Chapter ' + c.id)}`}
                                                    value={modalChapterCounts[c.id] ?? 0}
                                                    onChange={(e) => {
                                                      const v = Number(e.target.value);
                                                      setModalChapterCounts((prev) => ({ ...prev, [c.id]: Number.isNaN(v) ? 0 : v }));
                                                    }}
                                                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-shadow shadow-sm"
                                                  />
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Step 5: Difficulty selection before starting generation */}
                        {!isGenerating && modalStep === 5 && (
                          <div className="mb-4">
                            <div className="mb-3 justify-start">
                              <label className="mb-2 block text-sm font-medium text-gray-700">Choose difficulty distribution</label>
                              <div className="flex flex-wrap items-center gap-3">
                                {[
                                  { key: 'mixed', label: 'Mixed' },
                                  { key: 'easy', label: 'Easy-heavy' },
                                  { key: 'medium', label: 'Medium-heavy' },
                                  { key: 'hard', label: 'Hard-heavy' },
                                  { key: 'custom', label: 'Custom' },
                                ].map((opt) => {
                                  const sel = difficultyMode === (opt.key as any);
                                  return (
                                    <label key={opt.key} className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 border cursor-pointer select-none ${sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-600 border-blue-600'} `}>
                                      <input aria-label={`Choose ${opt.label}`} type="radio" name="diffMode" value={opt.key} checked={difficultyMode === (opt.key as any)} onChange={() => setDifficultyMode(opt.key as any)} className="hidden" />
                                      <span className="text-sm">{opt.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>

                            {difficultyMode === 'custom' && (
                              <div className="mb-3 grid grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-600">Easy (%)</label>
                                  <input type="number" min={0} max={100} value={difficultyPercents.easy} onChange={(e) => setDifficultyPercents((p) => ({ ...p, easy: Number(e.target.value) || 0 }))} className="w-full rounded border px-2 py-1" placeholder="%" aria-label="Easy percent" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">Medium (%)</label>
                                  <input type="number" min={0} max={100} value={difficultyPercents.medium} onChange={(e) => setDifficultyPercents((p) => ({ ...p, medium: Number(e.target.value) || 0 }))} className="w-full rounded border px-2 py-1" placeholder="%" aria-label="Medium percent" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">Hard (%)</label>
                                  <input type="number" min={0} max={100} value={difficultyPercents.hard} onChange={(e) => setDifficultyPercents((p) => ({ ...p, hard: Number(e.target.value) || 0 }))} className="w-full rounded border px-2 py-1" placeholder="%" aria-label="Hard percent" />
                                </div>
                              </div>
                            )}

                            
                          </div>
                        )}
                        
                        {/* Generation progress view: single overall progress bar + current chapter status */}
                        {isGenerating && (
                          <div className="mt-4 w-full">
                            <h4 className="mb-3 text-md font-medium text-gray-700">Generating questions — progress</h4>
                            <div className="p-3 border rounded-md bg-white">
                              <ProgressBar completed={overallCompletedCount} total={overallTotalCount || 1} label={`Overall progress`} striped />
                              <div className="mt-3 text-sm text-gray-600">{currentChapterStatusText || 'Starting generation...'}</div>
                              {/* keep chapterProgress referenced so lint/typechecks do not mark it unused */}
                              <span className="sr-only">{chapterProgress.length}</span>
                            </div>
                          </div>
                        )}
                        <div className="mt-6 flex items-center justify-end gap-3">
                        {/* Step navigation when not generating and before progress starts */}
                        {!isGenerating && modalStep === 1 && (
                          <>
                            <button onClick={() => { setModalError(null); setProgressStep(1); setModalStep(2); }} disabled={!modalGrade || !modalSubject} className={`rounded-md px-4 py-2 text-sm font-medium text-white ${modalGrade && modalSubject ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>Next</button>
                            <button onClick={() => { setShowGenerateModal(false); resetGeneration(); }} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300">Cancel</button>
                          </>
                        )}

                        {!isGenerating && modalStep === 2 && (
                          <>
                            <button onClick={() => { setModalError(null); setProgressStep(0); setModalStep(1); }} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300">Back</button>
                            <button onClick={() => { setModalError(null); setProgressStep(2); setModalStep(3); }} className={`rounded-md px-4 py-2 text-sm font-medium text-white ${modalExamName && modalExamTime ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`} disabled={!modalExamName || !modalExamTime}>Next</button>
                          </>
                        )}

                        {isGenerating && (
                          <button onClick={cancelGeneration} className="rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600">Cancel</button>
                        )}

                        {!isGenerating && modalStep === 3 && (
                          <>
                            <button onClick={() => { setModalError(null); setProgressStep(1); setModalStep(2); }} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300">Back</button>
                            <button onClick={() => { setModalError(null); setProgressStep(3); setModalStep(4); }} className={`rounded-md px-4 py-2 text-sm font-medium text-white ${modalSelectedGrades.length ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`} disabled={modalSelectedGrades.length === 0}>Next</button>
                          </>
                        )}

                        {!isGenerating && modalStep === 4 && (
                          <>
                            <button onClick={() => { setModalError(null); setProgressStep(2); setModalStep(3); }} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300">Back</button>
                            <button onClick={() => { setModalError(null); setProgressStep(4); setModalStep(5); }} className={`rounded-md px-4 py-2 text-sm font-medium text-white ${modalTotalQuestions && Object.keys(modalChapterCounts || {}).length ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`} disabled={!modalTotalQuestions || Object.keys(modalChapterCounts || {}).length === 0}>Next</button>
                          </>
                        )}

                        {!isGenerating && modalStep === 5 && (
                            <div className="mt-4 flex items-center justify-end gap-3">
                              <button onClick={() => { setModalError(null); setProgressStep(3); setModalStep(4); }} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300">Back</button>
                              <button onClick={() => {
                                setModalError(null);
                                // validate total vs chapter allocation before starting
                                const total = Number(modalTotalQuestions);
                                if (!modalTotalQuestions || Number.isNaN(total) || total <= 0) {
                                  setModalError('Please enter a valid total number of questions');
                                  return;
                                }
                                const sum = Object.values(modalChapterCounts || {}).reduce((s, v) => s + (Number(v) || 0), 0);
                                if (sum !== total) {
                                  setModalError(`The sum of per-chapter questions (${sum}) must equal the total (${total})`);
                                  return;
                                }
                                if (difficultyMode === 'custom') {
                                  const s = (difficultyPercents.easy || 0) + (difficultyPercents.medium || 0) + (difficultyPercents.hard || 0);
                                  if (s !== 100) {
                                    setModalError('Please ensure custom difficulty percentages add up to 100%');
                                    return;
                                  }
                                }
                                // attach chosen difficulty to some payload if needed (not implemented server-side here)
                                // move to Generating tab and start
                                setProgressStep(5);
                                setModalStep(6);
                                startGeneration();
                              }} className={`rounded-md px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700`}>Start</button>
                            </div>
                        )}

                        

                        
                      </div>
                      

                      {/* Show generated questions list after generation (styled like ExamDetail) */}
                      {!isGenerating && modalStep === 7 && generatedQuestions && generatedQuestions.length > 0 && (
                        <div className="mt-4">
                          <h4 className="mb-3 text-md font-medium text-gray-700">Generated questions ({generatedQuestions.length})</h4>
                          <div className="max-h-80 overflow-y-auto rounded-md border bg-white p-3">
                            {generatedQuestions.map((element: any, key: number) => (
                              <div key={key} className="container mx-auto px-2 mb-4">
                                <div className='pb-3'>
                                  <span className="bg-blue-200 text-blue-950 text-xs font-medium me-2 px-2.5 py-0.5 rounded-lg">Question <span className="font-bold">{key + 1} / {generatedQuestions.length}</span></span>
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.question || element.ques || '') }} />
                                <br />
                                <div className="flex flex-row w-full gap-5">
                                  <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                                    <div className='grid grid-cols-12'>
                                      <span className="col-span-1 font-bold text-md justify-center items-center">A.</span>
                                      <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.choice_a || element.choiceA || '') }} />
                                    </div>
                                  </div>
                                  <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                                    <div className='grid grid-cols-12'>
                                      <span className="col-span-1 font-bold text-md">B.</span>
                                      <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.choice_b || element.choiceB || '') }} />
                                    </div>
                                  </div>
                                </div>
                                <br />
                                <div className="flex flex-row w-full gap-5">
                                  <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                                    <div className='grid grid-cols-12'>
                                      <span className="col-span-1 font-bold text-md">C.</span>
                                      <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.choice_c || element.choiceC || '') }} />
                                    </div>
                                  </div>
                                  <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                                    <div className='grid grid-cols-12'>
                                      <span className="col-span-1 font-bold text-md">D.</span>
                                      <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.choice_d || element.choiceD || '') }} />
                                    </div>
                                  </div>
                                </div>
                                <br />
                                {/* Explanation */}
                                {element.explanation ? (
                                  <div className="mb-4 w-full rounded-lg border p-4 shadow-sm bg-white dark:bg-[#0b1220] border-gray-100 dark:border-neutral-800">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        <FaInfoCircle className="text-blue-600 dark:text-blue-400" />
                                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Explanation</h4>
                                      </div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">Rendered</span>
                                    </div>
                                    <div className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.explanation) }} />
                                  </div>
                                ) : null}

                                {/* Reference */}
                                {element.reference ? (
                                  <div className="mb-4 w-full rounded-lg border p-4 shadow-sm bg-white dark:bg-[#071025] border-gray-100 dark:border-neutral-800">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        <FaQuestion className="text-green-600 dark:text-green-400" />
                                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Reference</h4>
                                      </div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">Sources</span>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                                      {String(element.reference).split('||').map((ref: string, i: number) => {
                                        const s = ref.trim();
                                        if (!s) return null;
                                        return (
                                          <div key={i} className="mb-2">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">Source {i + 1}</div>
                                            <div className="prose prose-sm max-w-none text-gray-800 dark:text-gray-100" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s) }} />
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                ) : null}

                              </div>
                            ))}
                          </div>
                          <div className="mt-3 flex items-center justify-end gap-3">
                            <button onClick={() => { setGeneratedQuestions([]); setSaveError(null); setSaveSuccess(null); }} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300">Clear</button>
                            <button onClick={saveGeneratedExam} disabled={saveLoading} className={`rounded-md px-4 py-2 text-sm font-medium text-white ${saveLoading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
                              {saveLoading ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                          {saveError && <div className="mt-3 text-sm text-red-600">{saveError}</div>}
                          {saveSuccess && <div className="mt-3 text-sm text-green-600">{saveSuccess}</div>}
                        </div>
                      )}

                      {modalError && <div className="mt-3 text-sm text-red-600">{modalError}</div>}
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
